import {
  Component,
  OnInit,
  OnDestroy,
  Optional,
  Inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RulesEngineService } from '../rules-engine.service';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';

@Component({
    selector: 'gtw-validation-rule-edit-modal',
    templateUrl: './validation-rule-edit-modal.component.html',
    styleUrls: ['./validation-rule-edit-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ValidationRuleEditModalComponent implements OnInit, OnDestroy {
  loading = false;
  minimise = false;
  step = 1;
  mode: 'new' | 'edit' = 'new';

  ruleModel: any = {};
  private clientKey: any = '';

  allLinkedActions: any[] = [];
  allLinkedCacheObjects: any[] = [];
  newActionKey = '';
  newCacheObjectKey = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Optional() public dialogRef: MatDialogRef<ValidationRuleEditModalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private rulesService: RulesEngineService,
    private snackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {
    this.clientKey = data?.clientKey ?? '';
    this.mode = data?.mode || 'new';
  }

  get title(): string {
    return this.mode === 'new' ? 'Create Rule' : 'Edit Rule';
  }

  ngOnInit(): void {
    const rowData = this.data?.rowData || {};
    this.ruleModel = {
      RULE_KEY: this.mode === 'new' ? '' : (rowData.RULE_KEY || ''),
      RULE_NAME: this.mode === 'new' ? '' : (rowData.RULE_NAME || ''),
      RULE_DESC: this.mode === 'new' ? '' : (rowData.RULE_DESC || ''),
      RULE_PATH: this.mode === 'new' ? '' : (rowData.RULE_PATH || ''),
      RULE_ACTIVE_FLAG: this.mode === 'new' ? '1' : String(rowData.RULE_ACTIVE_FLAG ?? '1'),
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Step navigation ──

  saveAndNext(): void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.cdr.detectChanges();

    try {
      const ck = String(this.clientKey);
      const save$ = this.mode === 'new'
        ? this.rulesService.saveNewRule(ck, this.ruleModel)
        : this.rulesService.updateRule(ck, this.ruleModel);

      save$.pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          this.loading = false;
          if (String(res?.callSuccess) === '1') {
            if (this.mode === 'new' && res?.jsonObject?.[0]?.VALIDATION_RULE_KEY) {
              this.ruleModel.RULE_KEY = res.jsonObject[0].VALIDATION_RULE_KEY;
              this.mode = 'edit';
            }
            this.snackbar.openSnackBar('Rule saved successfully', 'gtw-snackbar--green');
            this.step = 2;
            this.cdr.detectChanges();
            this.loadLinkedData();
          } else {
            this.snackbar.openSnackBar(res?.errorMessage || 'Unable to save rule', 'gtw-snackbar--yellow');
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.loading = false;
          this.snackbar.openSnackBar('Unable to save rule', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
    } catch (e) {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  goNext(): void {
    if (this.step < 3) this.step++;
    if (this.step === 2) {
      this.loadLinkedData();
    }
    this.cdr.detectChanges();
  }

  goBack(): void {
    if (this.step > 1) this.step--;
    this.cdr.detectChanges();
  }

  end(): void {
    this.dialogRef?.close('saved');
  }

  cancel(): void {
    this.dialogRef?.close();
  }

  // ── Linked actions / cache ──

  private asString(value: any): string {
    return value === null || value === undefined ? '' : String(value).trim();
  }

  /**
   * Identifier keys (action_validation_key, drools_cache_key, ...) can legitimately
   * be 0. Distinguish "missing" (null/undefined/'') from a valid 0 so the delink is
   * not silently skipped (Admin migration guide Pitfall 9, Variant B).
   */
  private hasKey(value: any): boolean {
    return value !== null && value !== undefined && String(value).trim() !== '';
  }

  private pickFirst<T = any>(source: any, ...keys: string[]): T | undefined {
    for (const key of keys) {
      const value = source?.[key];
      if (value !== null && value !== undefined) {
        return value as T;
      }
    }
    return undefined;
  }

  private extractRulesPayload(data: any): any[] {
    if (Array.isArray(data?.jsonObject)) {
      return data.jsonObject;
    }
    if (data?.jsonObject && typeof data.jsonObject === 'object') {
      return [data.jsonObject];
    }
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  }

  private normalizeAction(action: any): any {
    return {
      ...action,
      action_key: this.asString(this.pickFirst(action, 'action_key', 'ACTION_KEY')),
      action_code: this.asString(this.pickFirst(action, 'action_code', 'ACTION_CODE')),
      action_name: this.asString(this.pickFirst(action, 'action_name', 'ACTION_NAME')),
      action_datasource: this.asString(this.pickFirst(action, 'action_datasource', 'ACTION_DATASOURCE', 'action_ds', 'ACTION_DS')),
      action_proc: this.asString(this.pickFirst(action, 'action_proc', 'ACTION_PROC')),
      action_validation_status: this.pickFirst(action, 'action_validation_status', 'ACTION_VALIDATION_STATUS') ?? 0,
      action_validation_key: this.pickFirst(action, 'action_validation_key', 'ACTION_VALIDATION_KEY'),
    };
  }

  private normalizeCacheObject(cache: any): any {
    return {
      ...cache,
      drools_cache_key: this.pickFirst(cache, 'drools_cache_key', 'DROOLS_CACHE_KEY', 'cache_key', 'CACHE_KEY'),
      cache_key: this.asString(this.pickFirst(cache, 'cache_key', 'CACHE_KEY', 'drools_cache_key', 'DROOLS_CACHE_KEY')),
      object_key: this.asString(this.pickFirst(cache, 'object_key', 'OBJECT_KEY')),
      cache_proc_call: this.asString(this.pickFirst(cache, 'cache_proc_call', 'CACHE_PROC_CALL')),
      object_filter_condition: this.asString(this.pickFirst(cache, 'object_filter_condition', 'OBJECT_FILTER_CONDITION')),
      is_cached: this.pickFirst(cache, 'is_cached', 'IS_CACHED'),
      is_stale: this.pickFirst(cache, 'is_stale', 'IS_STALE'),
      cache_status: this.asString(this.pickFirst(cache, 'cache_status', 'CACHE_STATUS')),
      last_updated: this.asString(this.pickFirst(cache, 'last_updated', 'LAST_UPDATED')),
    };
  }

  private loadLinkedData(): void {
    this.loading = true;
    this.cdr.detectChanges();

    const ck = String(this.clientKey);
    const rk = this.asString(this.ruleModel?.RULE_KEY);
    this.rulesService.getLinkedActions(ck, rk)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.loading = false;
          this.allLinkedActions = [];
          this.allLinkedCacheObjects = [];
          if (String(data?.callSuccess) === '1') {
            const ruleKey = this.asString(this.ruleModel?.RULE_KEY);
            const rules = this.extractRulesPayload(data);
            let matchedRule = rules.find((rule: any) =>
              this.asString(this.pickFirst(rule, 'validation_rule_key', 'VALIDATION_RULE_KEY', 'rule_key', 'RULE_KEY')) === ruleKey
            );

            if (!matchedRule && rules.length === 1) {
              matchedRule = rules[0];
            }

            const rawActions = this.pickFirst<any[]>(
              matchedRule,
              'validation_rule_actions',
              'VALIDATION_RULE_ACTIONS',
              'actions',
              'ACTIONS'
            );
            const rawCacheObjects = this.pickFirst<any[]>(
              matchedRule,
              'validation_rule_cache_objects',
              'VALIDATION_RULE_CACHE_OBJECTS',
              'cache_objects',
              'CACHE_OBJECTS'
            );

            this.allLinkedActions = Array.isArray(rawActions)
              ? rawActions.map((action: any) => this.normalizeAction(action))
              : [];
            this.allLinkedCacheObjects = Array.isArray(rawCacheObjects)
              ? rawCacheObjects.map((cache: any) => this.normalizeCacheObject(cache))
              : [];
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  addAction(): void {
    const actionKey = this.asString(this.newActionKey);
    if (!actionKey) {
      this.snackbar.openSnackBar('Provide action key', 'gtw-snackbar--yellow');
      return;
    }

    this.loading = true;
    this.cdr.detectChanges();

    const ck = String(this.clientKey);
    this.rulesService.addAction(ck, this.ruleModel?.RULE_KEY, actionKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          const added = Array.isArray(res?.jsonObject)
            ? res.jsonObject.length > 0
            : !!res?.jsonObject;
          if (String(res?.callSuccess) === '1' && added) {
            this.newActionKey = '';
            this.snackbar.openSnackBar('Action added successfully', 'gtw-snackbar--green');
            this.loadLinkedData();
          } else if (String(res?.callSuccess) === '1') {
            this.snackbar.openSnackBar('No action found for the provided Action ID', 'gtw-snackbar--yellow');
            this.cdr.detectChanges();
          } else {
            this.snackbar.openSnackBar(res?.errorMessage || 'Unable to add action', 'gtw-snackbar--yellow');
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.loading = false;
          this.snackbar.openSnackBar('Unable to add action', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  linkDelinkAction(action: any): void {
    const actionValidationKey = this.pickFirst(action, 'action_validation_key', 'ACTION_VALIDATION_KEY');
    if (!this.hasKey(actionValidationKey)) {
      this.snackbar.openSnackBar('Missing action link key', 'gtw-snackbar--yellow');
      return;
    }

    this.loading = true;
    this.cdr.detectChanges();

    // Toggle by current status: active (status==1) -> 0 (delink),
    // inactive (status==0) -> 1 (link). Matches legacy linkDelinkAction.
    const status = this.pickFirst(action, 'action_validation_status', 'ACTION_VALIDATION_STATUS');
    const isCurrentlyActive = String(status) === '1';
    const lnkOrDlnk = isCurrentlyActive ? 0 : 1;

    const ck = String(this.clientKey);
    this.rulesService.delinkAction(ck, actionValidationKey, lnkOrDlnk)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          if (String(res?.callSuccess) === '1') {
            this.snackbar.openSnackBar(isCurrentlyActive ? 'Action delinked' : 'Action linked', 'gtw-snackbar--green');
            this.loadLinkedData();
          } else {
            this.snackbar.openSnackBar(res?.errorMessage || 'Unable to update action', 'gtw-snackbar--yellow');
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.loading = false;
          this.snackbar.openSnackBar('Unable to update action', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  addCacheObject(): void {
    const cacheObjectKey = this.asString(this.newCacheObjectKey);
    if (!cacheObjectKey) {
      this.snackbar.openSnackBar('Provide cache key', 'gtw-snackbar--yellow');
      return;
    }

    this.loading = true;
    this.cdr.detectChanges();

    const ck = String(this.clientKey);
    const rk = this.asString(this.ruleModel?.RULE_KEY);
    this.rulesService.addCacheObject(ck, rk, cacheObjectKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          const added = Array.isArray(res?.jsonObject)
            ? res.jsonObject.length > 0
            : !!res?.jsonObject;
          if (String(res?.callSuccess) === '1' && added) {
            this.newCacheObjectKey = '';
            this.snackbar.openSnackBar('Cache object added successfully', 'gtw-snackbar--green');
            this.loadLinkedData();
          } else if (String(res?.callSuccess) === '1') {
            this.snackbar.openSnackBar('No cache object found for the provided Cache Key', 'gtw-snackbar--yellow');
            this.cdr.detectChanges();
          } else {
            this.snackbar.openSnackBar(res?.errorMessage || 'Unable to add cache object', 'gtw-snackbar--yellow');
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.loading = false;
          this.snackbar.openSnackBar('Unable to add cache object', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  delinkCacheObject(cache: any): void {
    const droolsCacheKey = this.pickFirst(cache, 'drools_cache_key', 'DROOLS_CACHE_KEY', 'cache_key', 'CACHE_KEY');
    if (!this.hasKey(droolsCacheKey)) {
      this.snackbar.openSnackBar('Missing cache link key', 'gtw-snackbar--yellow');
      return;
    }

    this.loading = true;
    this.cdr.detectChanges();

    const ck = String(this.clientKey);
    this.rulesService.delinkCacheObject(ck, this.ruleModel.RULE_KEY, droolsCacheKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          if (String(res?.callSuccess) === '1') {
            this.snackbar.openSnackBar('Cache object delinked', 'gtw-snackbar--green');
            this.loadLinkedData();
          } else {
            this.snackbar.openSnackBar(res?.errorMessage || 'Unable to delink cache object', 'gtw-snackbar--yellow');
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.loading = false;
          this.snackbar.openSnackBar('Unable to delink cache object', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }
}
